import { useEffect } from 'react'
import { Platform, useWindowDimensions, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated'
import { colors } from '@ui/colors'
import Typography from '@ui/typography'
import Gradient from '@ui/effects/Gradient'
import cn from '@ui/utils/cn'
import { gap } from '@ui/theme/values'
import { useDealDimensions } from '@screens/intro/deals/Deal'
import { timingConfig } from './styles'

interface Props {
  hidden: boolean
}

const getAnimation = (hidden: boolean) => (hidden ? 0 : 1)

const clWidth = Platform.select({
  default: 'w-max',
  web: 'w-full',
})

export default function RecommendedBadge({ hidden }: Props) {
  const { t } = useTranslation(['configuration'])
  const text = t('configuration:recommendation.badge')
  const screen = useWindowDimensions()
  const { height: dealHeight } = useDealDimensions(2, gap)
  const height = 32
  const top = screen.height / 2 - dealHeight / 2 - height / 2
  const strokeWidth = 2

  const animation = useSharedValue(getAnimation(hidden))

  const style = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: interpolate(animation.value, [0, 1], [top / 2, 0]),
        },
      ],
      opacity: interpolate(animation.value, [0, 1], [0, 1]),
    }
  })

  useEffect(() => {
    animation.set(
      withDelay(
        animation.value ? 0 : 500,
        withTiming(getAnimation(hidden), timingConfig)
      )
    )
  }, [hidden, animation])

  return (
    <Animated.View
      className="absolute flex w-full items-center justify-center"
      style={[style, { top }]}
    >
      <View
        className="relative flex items-center justify-center rounded-full px-4"
        style={{ height: height + strokeWidth * 2 }}
      >
        <Gradient
          colors={[colors.violet[300], colors.violet[700]]}
          start={[0, 0]}
          end={[1, 1]}
          className={cn(clWidth, 'rounded-full')}
        />
        <View
          className={cn(
            clWidth,
            'absolute bottom-0 left-0 right-0 top-0 h-full flex-1 items-center justify-center rounded-full'
          )}
          style={{ padding: strokeWidth }}
        >
          <Gradient
            colors={[colors.white, colors.white75, colors.white]}
            start={[0, 0]}
            end={[1, 1]}
            className={cn('relative inset-0 h-full rounded-full')}
          />
        </View>
        <Typography
          variant={Platform.select({ default: 'XSB', web: 'SMB' })}
          className="uppercase text-violet-700"
        >
          {text}
        </Typography>
        <Gradient
          colors={[
            colors.white75,
            colors.white25,
            colors.white0,
            colors.white25,
            colors.white75,
          ]}
          start={[0, 0]}
          end={[1, 1]}
          className={cn(clWidth, 'rounded-full')}
        />
      </View>
    </Animated.View>
  )
}
